/**
 * The transport, in the transcript's own footer.
 *
 * It shares the footer with Close and Download transcript rather than taking a bar of its own,
 * because a transcript viewer is a reading surface first: the player is one strip of controls at
 * the bottom of it, not a second chrome the words have to make room for. A recording nothing can
 * play renders none of this and the footer keeps the two buttons it always had.
 *
 * **The scrubber takes whatever width is left**, down to 240px, and below that the total is
 * dropped rather than the bar wrapping — the elapsed time is the number somebody reads while
 * listening, and the length is already stated in the header. The threshold is a container query
 * on the bar itself, so it answers to the panel's own width and not to the device's.
 */

import { ChevronDownIcon, PauseIcon, PlayIcon } from "lucide-react";
import { type PointerEvent as ReactPointerEvent, useCallback, useRef } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/spinner";
import { formatDuration } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import type { TranscriptPlayer } from "../hooks/use-transcript-player";
import {
  formatPlaybackRate,
  PLAYBACK_RATES,
  PLAYBACK_UNSUPPORTED_FORMAT_MESSAGE,
} from "../lib/transcript-playback";
import { PlaybackUnsupportedTooltip } from "./transcript-playback-tooltip";

export type TranscriptPlayerBarProps = {
  player: TranscriptPlayer;
  /** The recording's length as the header states it, used until the element measures its own. */
  totalSeconds: number | null;
};

/** How far a keyboard press moves the scrubber, and how far a shifted one does. */
const KEY_STEP_SECONDS = 5;
const KEY_STEP_LARGE_SECONDS = 30;

/** A time on the bar, in the same `m:ss` / `h:mm:ss` the header states the recording's Length in. */
function playbackTime(seconds: number): string {
  return formatDuration(Math.max(0, seconds) * 1_000, { style: "media" });
}

export function TranscriptPlayerBar({ player, totalSeconds }: TranscriptPlayerBarProps) {
  const isUnavailable = player.status === "unavailable";
  // The one refusal that has a fix a person can act on, so it is the one that says a sentence.
  const unsupported = player.unavailableReason === "unsupported_format";
  const isBuffering = player.status === "buffering";
  const isPlaying = player.status === "playing" || isBuffering;
  const total = player.duration > 0 ? player.duration : (totalSeconds ?? 0);
  // The handle holds at the position that was asked for while the seek buffers, so a released
  // scrubber never snaps back to where the element still is.
  const position = player.pendingSeek ?? player.currentTime;
  const ratio = total > 0 ? Math.min(1, Math.max(0, position / total)) : 0;

  return (
    <div
      data-testid="transcript-player-bar"
      className="@container/player flex min-w-0 flex-1 items-center gap-2.5"
    >
      <PlaybackUnsupportedTooltip enabled={unsupported}>
        <button
          type="button"
          data-testid="transcript-play-toggle"
          disabled={isUnavailable}
          // The label *is* the explanation where there is one: a disabled button called "Play"
          // that cannot play tells a screen-reader user nothing at all.
          aria-label={
            unsupported
              ? PLAYBACK_UNSUPPORTED_FORMAT_MESSAGE
              : isUnavailable
                ? "This recording cannot be played"
                : isBuffering
                  ? "Loading the recording"
                  : isPlaying
                    ? "Pause"
                    : "Play"
          }
          className={cn(
            "flex size-7 shrink-0 cursor-pointer items-center justify-center bg-primary text-primary-foreground focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            // A disabled control swallows the pointer, tooltip and all, so the wrapper takes the
            // hover instead.
            unsupported && "pointer-events-none"
          )}
          onClick={() => player.toggle()}
        >
          {isBuffering ? (
            <Spinner className="size-[13px]" aria-hidden />
          ) : isPlaying ? (
            <PauseIcon className="size-[13px]" aria-hidden />
          ) : (
            <PlayIcon className="size-[13px]" aria-hidden />
          )}
        </button>
      </PlaybackUnsupportedTooltip>

      <span
        data-testid="transcript-player-time"
        className="flex shrink-0 items-baseline gap-1 tabular-nums"
      >
        <span className="font-medium text-[0.71875rem]/[1.5] text-foreground">
          {isUnavailable ? "—" : playbackTime(position)}
        </span>
        {/* Dropped below the width the scrubber needs: the length is in the header already. */}
        <span
          data-testid="transcript-player-total"
          className="hidden text-[0.71875rem]/[1.5] text-neutral-dim @min-[26rem]/player:inline"
        >
          {isUnavailable || total <= 0 ? "/ —" : `/ ${playbackTime(total)}`}
        </span>
      </span>

      <PlaybackScrubber
        player={player}
        total={total}
        position={position}
        ratio={ratio}
        disabled={isUnavailable}
      />

      <DropdownMenu>
        <DropdownMenuTrigger
          data-testid="transcript-speed-trigger"
          disabled={isUnavailable}
          aria-label={`Playback speed, ${formatPlaybackRate(player.rate)}`}
          className="flex h-6 w-[46px] shrink-0 cursor-pointer items-center justify-center gap-0.5 border border-border disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2"
        >
          <span className="font-medium text-[10.5px] text-neutral-muted-fg">
            {formatPlaybackRate(player.rate)}
          </span>
          <ChevronDownIcon className="size-[9px] text-neutral-dim" aria-hidden />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="top" className="min-w-[5rem]">
          {PLAYBACK_RATES.map((rate) => (
            <DropdownMenuItem
              key={rate}
              data-testid="transcript-speed-option"
              data-selected={rate === player.rate || undefined}
              className={cn("text-xs", rate === player.rate && "font-medium text-primary")}
              onClick={() => player.setRate(rate)}
            >
              {formatPlaybackRate(rate)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * The bar the recording is dragged along.
 *
 * The hit area is 20px tall around a 4px track, because a 4px pointer target is a target nobody
 * hits; the drag starts from anywhere in it, the track included, and the transcript deliberately
 * does **not** follow until the pointer is released — the reader is looking at the bar, not at
 * the words, and scrolling the column under them at drag speed is unreadable.
 */
function PlaybackScrubber({
  player,
  total,
  position,
  ratio,
  disabled,
}: {
  player: TranscriptPlayer;
  total: number;
  position: number;
  ratio: number;
  disabled: boolean;
}) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);

  const seekToPointer = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const track = trackRef.current;
      if (track === null || total <= 0) return;
      const bounds = track.getBoundingClientRect();
      if (bounds.width <= 0) return;
      const fraction = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width));
      player.seek(fraction * total);
    },
    [player, total]
  );

  return (
    <div
      ref={trackRef}
      data-testid="transcript-scrubber"
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label="Seek through the recording"
      aria-valuemin={0}
      aria-valuemax={Math.max(0, Math.round(total))}
      aria-valuenow={Math.round(position)}
      aria-valuetext={playbackTime(position)}
      aria-disabled={disabled || undefined}
      className={cn(
        "relative flex h-5 min-w-[240px] flex-1 shrink cursor-pointer items-center",
        "focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2",
        disabled && "cursor-not-allowed opacity-50"
      )}
      onPointerDown={(event) => {
        if (disabled) return;
        event.preventDefault();
        draggingRef.current = true;
        event.currentTarget.setPointerCapture(event.pointerId);
        player.beginScrub();
        seekToPointer(event);
      }}
      onPointerMove={(event) => {
        if (!draggingRef.current) return;
        seekToPointer(event);
      }}
      onPointerUp={(event) => {
        if (!draggingRef.current) return;
        draggingRef.current = false;
        seekToPointer(event);
        player.endScrub();
      }}
      onPointerCancel={() => {
        if (!draggingRef.current) return;
        draggingRef.current = false;
        player.endScrub();
      }}
      onKeyDown={(event) => {
        if (disabled) return;
        const step = event.shiftKey ? KEY_STEP_LARGE_SECONDS : KEY_STEP_SECONDS;
        if (event.key === "ArrowLeft") player.seek(position - step);
        else if (event.key === "ArrowRight") player.seek(position + step);
        else if (event.key === "Home") player.seek(0);
        else if (event.key === "End") player.seek(total);
        else return;
        event.preventDefault();
        // The dialog listens for the same keys; a focused scrubber has already answered them.
        event.stopPropagation();
      }}
    >
      <div className="relative h-1 w-full bg-muted">
        <div
          data-testid="transcript-scrubber-played"
          className="absolute inset-y-0 left-0 bg-primary"
          style={{ width: `${ratio * 100}%` }}
        />
        <span
          data-testid="transcript-scrubber-handle"
          aria-hidden
          className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 h-3 w-[3px] bg-foreground"
          style={{ left: `${ratio * 100}%` }}
        />
      </div>
    </div>
  );
}
